import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import Button from '@/components/ui/Button';
import { ProjectDtoIn } from '@/models/in/ProjectDtoIn';
import { UpdateProjectStatusDtoOut } from '@/models/out/ProjectDtoOut';
import { ProjectStatus } from '@/models/common/ProjectStatus';
import { usePutAuthApi } from '@/hooks/UseApiCalls';

interface ProjectStatusChangeDialogProps {
  open: boolean;
  onClose: () => void;
  project: ProjectDtoIn;
  onSuccess: () => void;
}

/**
 * Moves one project along its approval workflow. The options come from the server-computed
 * allowedNextStatuses on the project, so the dialog never hard-codes the workflow.
 */
const ProjectStatusChangeDialog = ({
  open,
  onClose,
  project,
  onSuccess,
}: ProjectStatusChangeDialogProps) => {
  const { t } = useTranslation();

  const [status, setStatus] = useState<ProjectStatus | ''>('');
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { mutateAsync: updateStatus, isPending } = usePutAuthApi<
    ProjectDtoIn, object, UpdateProjectStatusDtoOut
  >(`management/projects/${project.id}/status`);

  useEffect(() => {
    if (open) {
      setStatus(project.allowedNextStatuses[0] ?? '');
      setComment('');
      setError(null);
    }
  }, [open, project]);

  const handleClose = () => {
    setError(null);
    onClose();
  };

  // A question to the coordinator is the comment itself, so it cannot be empty (SPR-10302).
  const isQuestion = status === ProjectStatus.PendingCoordinatorResponse;
  const isMissingQuestion = isQuestion && !comment.trim();

  const handleSubmit = async () => {
    if (!status || isMissingQuestion) return;

    setError(null);
    try {
      await updateStatus({ params: {}, body: { status, comment: comment.trim() || null } });
      onSuccess();
      handleClose();
    } catch (err) {
      console.error('Error updating project status:', err);
      setError(t('projectsManagement.statusDialog.error'));
    }
  };

  return (
    <MainDialogLayout
      open={open}
      onOpenChange={(isOpen: boolean) => {
        if (!isOpen) handleClose();
      }}
      title={t('projectsManagement.statusDialog.title')}
    >
      <div className="flex flex-col gap-4 p-4">
        <p className="body-md-semibold">{project.name}</p>

        <div className="flex flex-col gap-1">
          <label htmlFor="project-status" className="body-sm-semibold text-(--font-secondary)">
            {t('projectsManagement.statusDialog.statusLabel')}
          </label>
          <select
            id="project-status"
            className="border border-(--border-main) rounded-lg px-3 py-2 body-sm-regular"
            value={status}
            onChange={(e) => setStatus(e.target.value as ProjectStatus)}
          >
            {project.allowedNextStatuses.map((nextStatus) => (
              <option key={nextStatus} value={nextStatus}>
                {t(`projects.status.${nextStatus}`)}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="project-status-comment" className="body-sm-semibold text-(--font-secondary)">
            {isQuestion ? t('projectsManagement.statusDialog.questionLabel') : t('projectsManagement.statusDialog.commentLabel')}
            {isQuestion && <span className="text-red-500" aria-hidden="true"> *</span>}
          </label>
          <textarea
            id="project-status-comment"
            rows={3}
            className="border border-(--border-main) rounded-lg px-3 py-2 body-sm-regular resize-none"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            aria-required={isQuestion}
            placeholder={isQuestion
              ? t('projectsManagement.statusDialog.questionPlaceholder')
              : t('projectsManagement.statusDialog.commentPlaceholder')}
          />
          {isQuestion && (
            <p className="body-xs-regular text-(--font-secondary)">{t('projectsManagement.statusDialog.questionHint')}</p>
          )}
        </div>

        {error && <p className="text-red-500 body-sm-regular">{error}</p>}

        <div className="flex gap-3 justify-end mt-2">
          <Button variant="white" onClick={handleClose} disabled={isPending}>
            {t('close')}
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={isPending || !status || isMissingQuestion}>
            {isPending ? t('saving') : t('projectsManagement.statusDialog.submit')}
          </Button>
        </div>
      </div>
    </MainDialogLayout>
  );
};

export default ProjectStatusChangeDialog;
